import { useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Github,
  ExternalLink,
  ArrowRight,
  GitBranch,
  Radio,
  Network,
  ArrowLeftRight,
  Anchor,
  Route,
  Check,
  Copy,
  Terminal,
  FolderGit2,
  X,
  Minus,
  Plus,
  Play,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { Section } from './ui/Section';

const REPO_URL = 'https://github.com/Rakshan001/Baton-Multi-Agent-';
const DOCS_URL = 'https://baton-landing.vercel.app';
const CASE_STUDY_URL = '/project/12/baton';

/* Figures verified against the repository, not estimated. */
const stats = [
  { value: '641', unit: '', label: 'tests passing' },
  { value: '16.1', unit: 'K', label: 'lines of TypeScript' },
  { value: '15', unit: '', label: 'agents in parallel' },
  { value: '6', unit: '', label: 'agents wired in' },
];

/* Real CLI commands mapped to simulated terminal logs and metrics */
const COMMAND_DEMOS = {
  'baton pass': {
    command: 'baton pass my-task --to cursor',
    icon: ArrowLeftRight,
    badge: 'handoff',
    desc: 'Pass task from expensive reasoning agent to lightweight executor',
    lines: [
      { text: 'baton pass my-task --to cursor', type: 'command' },
      { text: 'reading plan from claude-code session (2,410 tokens parsed)', type: 'step' },
      { text: 'handoff written: plan, checklist and what is left', type: 'step' },
      { text: 'workspace ready for cursor: my-task [feat/my-task]', type: 'worktree' },
      { text: 'handoff complete: cursor ready to execute with zero context bleed', type: 'success' },
      { text: 'Estimated savings: ~$1.42 (84% reduction vs all-reasoning run)', type: 'metric' },
    ],
  },
  'baton serve': {
    command: 'baton serve --port 3847',
    icon: Radio,
    badge: 'daemon',
    desc: 'Live view of which agent is editing which file',
    lines: [
      { text: 'baton serve --port 3847', type: 'command' },
      { text: 'dashboard live: watching 2 agents', type: 'daemon' },
      { text: '[10:24:02] claude-code: reading src/planner.ts', type: 'step' },
      { text: '[10:24:08] cursor: editing tests/planner.test.ts', type: 'step' },
      { text: 'overlap check: no conflicts, each agent on its own files', type: 'success' },
    ],
  },
  'baton new': {
    command: 'baton new auth-v2 --from main',
    icon: GitBranch,
    badge: 'worktree',
    desc: 'A separate workspace and branch for every task',
    lines: [
      { text: 'baton new auth-v2 --from main', type: 'command' },
      { text: 'creating a separate workspace for auth-v2', type: 'step' },
      { text: 'branch feat/auth-v2 ready', type: 'worktree' },
      { text: 'project skills and rules loaded', type: 'step' },
      { text: 'ready: several agents can now work at once without conflicts', type: 'success' },
    ],
  },
  'baton kb': {
    command: 'baton kb query "auth middleware callers"',
    icon: Network,
    badge: 'graph',
    desc: 'Find the right code in milliseconds, without spending tokens',
    lines: [
      { text: 'baton kb query "auth middleware callers"', type: 'command' },
      { text: 'codebase map searched in 4ms (0 tokens used)', type: 'metric' },
      { text: 'Target: src/middleware/auth.ts (exports: verifyToken, requireRole)', type: 'step' },
      { text: '-> callers: src/routes/api.ts:14, src/routes/user.ts:28', type: 'step' },
      { text: '-> dependencies: src/lib/jwt.ts, src/config/auth.ts', type: 'step' },
      { text: 'sent the agent only the code it needs', type: 'success' },
    ],
  },
  'baton memory': {
    command: 'baton memory recall "auth token format"',
    icon: Anchor,
    badge: 'evidence',
    desc: 'Project memory that knows when it is out of date',
    lines: [
      { text: 'baton memory recall "auth token format"', type: 'command' },
      { text: 'checking the remembered fact against the current code', type: 'step' },
      { text: 'code unchanged: the fact still holds', type: 'success' },
      { text: 'fact: JWT payload requires "tenant_id" and "role" claims', type: 'step' },
      { text: 'guarantee: stale facts withheld automatically on file change', type: 'metric' },
    ],
  },
  'baton route': {
    command: 'baton route "Fix failing integration test"',
    icon: Route,
    badge: 'router',
    desc: 'Picks the right agent for each task, at no model cost',
    lines: [
      { text: 'baton route "Fix failing integration test in auth.test.ts"', type: 'command' },
      { text: 'matching the task to the agent that suits it', type: 'step' },
      { text: 'task type: test fix, cost tier: lowest', type: 'metric' },
      { text: 'assigned agent: Cursor (confidence: 96/100, zero LLM overhead)', type: 'success' },
      { text: 'decision latency: 2ms (free, auditable routing log saved)', type: 'metric' },
    ],
  },
};

/* Each capability is titled by the command that performs it */
const capabilities = [
  {
    command: 'baton new',
    icon: GitBranch,
    title: 'Separate workspaces',
    body: 'Every task gets its own workspace and branch, so several agents work at once without touching each other’s files.',
  },
  {
    command: 'baton serve',
    icon: Radio,
    title: 'Live edit signals',
    body: 'A live dashboard shows which agent is editing what. When two reach for the same file, one waits until the other is done.',
  },
  {
    command: 'baton kb',
    icon: Network,
    title: 'Codebase map',
    body: 'Agents find the right file from a compact map of the project, in about a thousand tokens instead of reading everything.',
  },
  {
    command: 'baton pass',
    icon: ArrowLeftRight,
    title: 'One-file handoff',
    body: 'Packs the plan, the checklist and the progress so far into one handoff, with a cost estimate, for the next agent.',
  },
  {
    command: 'baton memory',
    icon: Anchor,
    title: 'Memory that stays true',
    body: 'Shared project facts are checked against the current code before an agent relies on them, so stale notes are never repeated as truth.',
  },
  {
    command: 'baton route',
    icon: Route,
    title: 'Smart routing',
    body: 'Sends each task to the agent that suits it, instantly and at no model cost, and tells you why it chose that agent.',
  },
];

/* 6 wired agents roster */
const wiredAgents = [
  { name: 'Claude Code', specialty: 'Plan / Architecture', role: 'Reasoning' },
  { name: 'Cursor', specialty: 'Fast Inline Edits', role: 'Execution' },
  { name: 'Codex', specialty: 'Batch Scaffolding', role: 'Execution' },
  { name: 'Gemini CLI', specialty: 'Multimodal / Deep Context', role: 'Analysis' },
  { name: 'Aider', specialty: 'Terminal Git Pairing', role: 'Execution' },
  { name: 'OpenCode', specialty: 'Local Offline Weights', role: 'Execution' },
];

function Relay() {
  return (
    <div
      className="relative rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-elevated)] px-5 py-7 sm:px-10 sm:py-9 shadow-[var(--shadow-sm)]"
      role="img"
      aria-label="A baton passes from Claude Code, which plans the work, along a rail through HANDOFF.md, to Cursor, which executes it."
    >
      <div className="mx-auto flex max-w-3xl flex-col items-stretch sm:flex-row sm:items-center">
        {/* The agent that plans */}
        <div
          aria-hidden="true"
          className="baton-relay-plan rounded-lg border border-[var(--baton-hairline)] bg-[var(--baton-node-rest)] px-4 py-3 text-center sm:w-52 sm:text-left transition-colors duration-300"
        >
          <div className="flex items-center justify-between gap-2 sm:justify-start">
            <span className="baton-mono text-[10px] tracking-[0.18em] text-[var(--fg-secondary)] uppercase">
              phase 01 / plan
            </span>
            <span className="baton-mono text-[10px] rounded border border-[var(--baton-hairline)] px-1.5 py-0.5 text-[var(--fg-muted)]">
              Sonnet 3.7
            </span>
          </div>
          <p className="baton-display mt-1.5 text-base font-medium text-[var(--fg-primary)]">Claude Code</p>
          <p className="baton-mono mt-0.5 text-xs text-[var(--fg-secondary)]">architecture / reasoning</p>
        </div>

        {/* The rail */}
        <div className="relative mx-auto my-3 h-20 w-px self-center sm:mx-0 sm:my-0 sm:h-px sm:w-auto sm:flex-1 sm:self-auto">
          <div aria-hidden="true" className="absolute inset-0 bg-[var(--baton-hairline)]" />
          <div
            aria-hidden="true"
            className="baton-relay-fill absolute inset-0 bg-[var(--fg-primary)]"
          />

          {/* HANDOFF.md mid-rail */}
          <div
            aria-hidden="true"
            className="baton-relay-handoff absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded-md border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-3 py-1.5 whitespace-nowrap shadow-[var(--shadow-sm)]"
          >
            <div className="flex items-center gap-1.5">
              <span className="baton-mono text-xs font-medium text-[var(--fg-primary)]">
                HANDOFF.md
              </span>
              <span className="baton-mono text-[10px] text-[var(--fg-muted)]">
                [plan + budget]
              </span>
            </div>
          </div>

          {/* The baton token */}
          <div
            aria-hidden="true"
            className="baton-relay-token absolute top-1/2 left-1/2 z-30 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--fg-primary)] shadow-[var(--shadow-sm)]"
          />
        </div>

        {/* The agent that executes */}
        <div
          aria-hidden="true"
          className="baton-relay-exec rounded-lg border border-[var(--baton-hairline)] bg-[var(--baton-node-rest)] px-4 py-3 text-center sm:w-52 sm:text-right transition-colors duration-300"
        >
          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <span className="baton-mono text-[10px] rounded border border-[var(--baton-hairline)] px-1.5 py-0.5 text-[var(--fg-muted)]">
              Fast Model
            </span>
            <span className="baton-mono text-[10px] tracking-[0.18em] text-[var(--fg-secondary)] uppercase">
              phase 02 / execute
            </span>
          </div>
          <p className="baton-display mt-1.5 text-base font-medium text-[var(--fg-primary)]">Cursor</p>
          <p className="baton-mono mt-0.5 text-xs text-[var(--fg-secondary)]">surgical edit / tests</p>
        </div>
      </div>

      {/* Cost-efficiency breakdown strip */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--baton-hairline)] pt-5 text-xs">
        <div className="flex items-center gap-2 text-[var(--fg-secondary)]">
          <span className="baton-mono font-medium text-[var(--fg-primary)]">Economic Arbitrage:</span>
          <span>Spend reasoning budget on the blueprint; spend low-cost tokens on the keystrokes.</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-3 py-1">
          <span className="baton-mono text-[11px] text-[var(--fg-secondary)]">Cost reduction:</span>
          <span className="baton-mono text-[11px] font-semibold text-[var(--fg-primary)]">~84% per task</span>
        </div>
      </div>
    </div>
  );
}

export default function BatonSpotlight() {
  const reduceMotion = useReducedMotion();
  const [activeCommand, setActiveCommand] = useState('baton pass');
  const [copied, setCopied] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);

  const activeDemo = COMMAND_DEMOS[activeCommand] || COMMAND_DEMOS['baton pass'];

  const handleCopyCommand = (cmdText) => {
    navigator.clipboard.writeText(cmdText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRerun = () => {
    setIsSimulating(true);
    setTimeout(() => setIsSimulating(false), 300);
  };

  const handleSelectCapability = (commandName) => {
    setActiveCommand(commandName);
    const terminalEl = document.getElementById('baton-terminal-demo');
    if (terminalEl) {
      terminalEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const reveal = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: reduceMotion ? 0 : 0.5, ease: 'easeOut' },
    },
  };

  return (
    <Section id="opensource" className="baton-scope relative overflow-hidden">
      <div className="relative z-10">
        {/* Clean Eyebrow row with zero decorative dots */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--baton-hairline)] pb-5"
        >
          <p className="baton-mono text-xs tracking-[0.22em] text-[var(--fg-primary)] uppercase">
            // flagship / open source
          </p>
          <p className="baton-mono text-xs tracking-[0.14em] text-[var(--fg-secondary)] uppercase">
            MIT / TypeScript / self-hosted
          </p>
        </motion.div>

        {/* Thesis & Headline */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.05 }}
          className="pt-10"
        >
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="baton-display text-5xl font-semibold tracking-tight text-[var(--fg-primary)] sm:text-6xl">
              Baton
            </h2>
            <span className="baton-mono rounded-full border border-[var(--baton-hairline)] bg-[var(--bg-elevated)] px-3 py-1 text-xs text-[var(--fg-secondary)]">
              v0.9.4 release
            </span>
          </div>

          <p className="baton-display mt-5 max-w-2xl text-2xl leading-tight font-medium text-[var(--fg-primary)] sm:text-[2.1rem]">
            Plan on your expensive agent.
            <br />
            Pass the baton to your cheap one.
          </p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--fg-secondary)]">
            Baton lets Claude Code, Cursor, Codex, Gemini, Aider and OpenCode coordinate across the same
            repository without clobbering each other. Every task gets its own workspace, a live dashboard
            shows who is editing what, and one{' '}
            <span className="baton-mono text-sm font-medium text-[var(--fg-primary)]">HANDOFF.md</span>{' '}
            carries the plan &mdash; with a cost estimate &mdash; from the agent that reasons to the agent
            that implements.
          </p>
        </motion.div>

        {/* Animated Multi-Agent Relay */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          transition={{ delay: 0.1 }}
          className="mt-12"
        >
          <Relay />
        </motion.div>

        {/* Authentic macOS Developer Terminal Simulator */}
        <motion.div
          id="baton-terminal-demo"
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.12 }}
          className="mt-6 overflow-hidden rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] shadow-[var(--shadow-sm)]"
        >
          {/* Terminal Window Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--baton-hairline)] bg-[var(--bg-elevated)] px-4 py-2.5">
            <div className="flex items-center gap-4">
              {/* macOS Window Controls with Authentic Hover Micro-Icons */}
              <div
                className="group/controls flex items-center gap-1.5 cursor-pointer"
                aria-label="Terminal window controls"
              >
                <div
                  className="terminal-btn-close flex h-3 w-3 items-center justify-center rounded-full border transition-opacity duration-200"
                  title="Close session"
                >
                  <X className="h-2 w-2 opacity-0 transition-opacity duration-150 group-hover/controls:opacity-100" />
                </div>
                <div
                  className="terminal-btn-minimize flex h-3 w-3 items-center justify-center rounded-full border transition-opacity duration-200"
                  title="Minimize"
                >
                  <Minus className="h-2 w-2 opacity-0 transition-opacity duration-150 group-hover/controls:opacity-100" />
                </div>
                <div
                  className="terminal-btn-maximize flex h-3 w-3 items-center justify-center rounded-full border transition-opacity duration-200"
                  title="Expand"
                >
                  <Plus className="h-2 w-2 opacity-0 transition-opacity duration-150 group-hover/controls:opacity-100" />
                </div>
              </div>

              {/* Terminal Tab Badge & Path Breadcrumbs */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-md border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-2.5 py-1 text-xs">
                  <Terminal className="h-3.5 w-3.5 text-[var(--fg-primary)] shrink-0" />
                  <span className="baton-mono font-medium text-[var(--fg-primary)]">zsh</span>
                  <span className="text-[var(--fg-muted)]">/</span>
                  <FolderGit2 className="h-3 w-3 text-[var(--fg-secondary)] shrink-0" />
                  <span className="baton-mono text-[var(--fg-secondary)]">baton</span>
                  <div className="flex items-center gap-1 border-l border-[var(--baton-hairline)] pl-1.5 text-[10px] text-[var(--fg-muted)]">
                    <GitBranch className="h-2.5 w-2.5 text-[var(--fg-secondary)]" />
                    <span>main</span>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[var(--fg-muted)]">
                  <Radio className="h-3 w-3 text-[var(--fg-primary)]" />
                  <span className="baton-mono">dashboard live</span>
                </div>
              </div>
            </div>

            {/* Action Buttons (Run & Copy) */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRerun}
                className="baton-mono inline-flex items-center gap-1.5 rounded-md border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-2.5 py-1 text-[11px] text-[var(--fg-secondary)] transition-colors duration-200 hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)] focus-visible:outline-none"
                title="Re-run terminal simulation"
              >
                <Play className="h-3 w-3 text-[var(--fg-secondary)]" />
                <span>Run</span>
              </button>

              <button
                type="button"
                onClick={() => handleCopyCommand(activeDemo.command)}
                className="baton-mono inline-flex items-center gap-1.5 rounded-md border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-2.5 py-1 text-[11px] text-[var(--fg-secondary)] transition-colors duration-200 hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)] focus-visible:outline-none"
                aria-label="Copy terminal command"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-[var(--fg-primary)]" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5 text-[var(--fg-secondary)]" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Command Selector Tabs with Matching Lucide Icons */}
          <div className="flex flex-wrap items-center gap-1 border-b border-[var(--baton-hairline)] bg-[var(--bg-elevated)] px-3 py-2 overflow-x-auto">
            {Object.entries(COMMAND_DEMOS).map(([cmd, data]) => {
              const isSelected = activeCommand === cmd;
              const TabIcon = data.icon;
              return (
                <button
                  key={cmd}
                  type="button"
                  onClick={() => setActiveCommand(cmd)}
                  className={`baton-mono inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs whitespace-nowrap transition-colors duration-200 ${
                    isSelected
                      ? 'bg-[var(--fg-primary)] text-[var(--bg-canvas)] font-medium shadow-[var(--shadow-sm)]'
                      : 'text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)]'
                  }`}
                >
                  <TabIcon className={`h-3 w-3 shrink-0 ${isSelected ? 'text-[var(--bg-canvas)]' : 'text-[var(--fg-secondary)]'}`} />
                  <span>{cmd}</span>
                </button>
              );
            })}
          </div>

          {/* Terminal Output Area with Rich Syntax Icons */}
          <div className="p-5 font-mono">
            <div className="mb-3 flex items-center gap-2 text-[11px] text-[var(--fg-muted)]">
              <ChevronRight className="h-3 w-3 shrink-0" />
              <span className="baton-mono">{activeDemo.desc}</span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeCommand + (isSimulating ? '-sim' : '')}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-2.5"
              >
                {activeDemo.lines.map((line, idx) => {
                  let IconComponent = ChevronRight;
                  let iconClass = 'text-[var(--fg-muted)]';
                  let textClass = 'text-[var(--fg-secondary)]';

                  if (line.type === 'command') {
                    IconComponent = Terminal;
                    iconClass = 'text-[var(--fg-primary)]';
                    textClass = 'text-[var(--fg-primary)] font-medium';
                  } else if (line.type === 'success') {
                    IconComponent = Check;
                    iconClass = 'text-[var(--fg-primary)]';
                    textClass = 'text-[var(--fg-primary)] font-medium';
                  } else if (line.type === 'metric') {
                    IconComponent = Sparkles;
                    iconClass = 'text-[var(--fg-primary)]';
                    textClass = 'text-[var(--fg-primary)] font-medium';
                  } else if (line.type === 'worktree') {
                    IconComponent = GitBranch;
                    iconClass = 'text-[var(--fg-secondary)]';
                    textClass = 'text-[var(--fg-secondary)]';
                  } else if (line.type === 'daemon') {
                    IconComponent = Radio;
                    iconClass = 'text-[var(--fg-secondary)]';
                    textClass = 'text-[var(--fg-secondary)]';
                  }

                  return (
                    <div key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm leading-relaxed">
                      <IconComponent className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${iconClass}`} />
                      <span className={`baton-mono ${textClass}`}>{line.text}</span>
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>

            {/* Prompt Footer Line */}
            <div className="mt-4 flex items-center gap-2 pt-3 text-xs border-t border-[var(--baton-hairline)] text-[var(--fg-muted)]">
              <span className="baton-mono text-[var(--fg-primary)] font-medium">$</span>
              <span className="baton-mono text-[var(--fg-secondary)]">{activeDemo.command}</span>
              <span
                aria-hidden="true"
                className="baton-caret inline-block h-3.5 w-[6px] bg-[var(--fg-primary)]"
              />
            </div>
          </div>
        </motion.div>

        {/* 6 Wired Agents Support Roster */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.14 }}
          className="mt-8 rounded-lg border border-[var(--baton-hairline)] bg-[var(--bg-elevated)] p-5 shadow-[var(--shadow-sm)]"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--baton-hairline)] pb-3">
            <span className="baton-mono text-xs tracking-[0.16em] text-[var(--fg-secondary)] uppercase">
              supported agent matrix
            </span>
            <span className="baton-mono text-[11px] text-[var(--fg-muted)]">
              6 agents wired in, no conflicts
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {wiredAgents.map((agent) => (
              <div
                key={agent.name}
                className="rounded-md border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] p-3 text-left transition-colors duration-200 hover:border-[var(--baton-signal)]"
              >
                <p className="baton-display text-sm font-medium text-[var(--fg-primary)]">{agent.name}</p>
                <p className="baton-mono mt-1 text-[10px] text-[var(--fg-secondary)]">{agent.specialty}</p>
                <span className="baton-mono mt-2 inline-block text-[9px] uppercase tracking-wider text-[var(--fg-muted)]">
                  {agent.role}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Figures & Repository Metrics */}
        <motion.dl
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.16 }}
          className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[var(--baton-hairline)] bg-[var(--baton-hairline)] sm:grid-cols-4 shadow-[var(--shadow-sm)]"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="bg-[var(--bg-elevated)] px-5 py-6">
              <dt className="sr-only">{stat.label}</dt>
              <dd>
                <span className="baton-display text-4xl font-semibold tracking-[0.01em] text-[var(--fg-primary)] tabular-nums">
                  {stat.value}
                </span>
                {stat.unit && (
                  <span className="baton-display text-2xl font-semibold text-[var(--fg-primary)]">
                    {stat.unit}
                  </span>
                )}
                <span className="baton-mono mt-2 block text-[11px] leading-snug tracking-wide text-[var(--fg-secondary)]">
                  {stat.label}
                </span>
              </dd>
            </div>
          ))}
        </motion.dl>

        {/* Primary Action Buttons */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.18 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="baton-display group inline-flex items-center gap-2 rounded-lg bg-[var(--fg-primary)] px-6 py-3 font-medium text-[var(--bg-canvas)] transition-colors duration-200 hover:opacity-90 focus-visible:outline-none"
          >
            Read the docs
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={REPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="baton-display inline-flex items-center gap-2 rounded-lg border border-[var(--baton-hairline)] px-6 py-3 font-medium text-[var(--fg-primary)] transition-colors duration-200 hover:bg-[var(--bg-hover)] focus-visible:outline-none"
          >
            <Github className="h-4 w-4" />
            View source
          </a>
          <Link
            to={CASE_STUDY_URL}
            className="baton-display group inline-flex items-center gap-2 rounded-lg px-4 py-3 font-medium text-[var(--fg-secondary)] transition-colors duration-200 hover:text-[var(--fg-primary)] focus-visible:outline-none"
          >
            Read the case study
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Interactive Capability Showcase (Linked to Terminal) */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ delay: 0.1 }}
          className="mt-20"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="baton-mono text-xs tracking-[0.22em] text-[var(--fg-secondary)] uppercase">
              // six commands, six capabilities
            </h3>
            <span className="baton-mono text-[11px] text-[var(--fg-muted)]">
              click any command card to simulate CLI execution
            </span>
          </div>

          <div className="mt-6 grid gap-px overflow-hidden rounded-lg border border-[var(--baton-hairline)] bg-[var(--baton-hairline)] sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ command, icon: Icon, title, body }) => {
              const isCurrent = activeCommand === command;
              return (
                <button
                  type="button"
                  key={command}
                  onClick={() => handleSelectCapability(command)}
                  className={`group text-left p-6 transition-colors duration-200 cursor-pointer ${
                    isCurrent
                      ? 'bg-[var(--bg-hover)] ring-1 ring-inset ring-[var(--baton-signal)]'
                      : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <Icon className="h-4 w-4 shrink-0 text-[var(--fg-secondary)] transition-colors duration-200 group-hover:text-[var(--fg-primary)]" />
                      <code className="baton-mono text-xs font-medium text-[var(--fg-primary)]">{command}</code>
                    </div>
                    {isCurrent && (
                      <span className="baton-mono text-[10px] rounded border border-[var(--baton-hairline)] bg-[var(--bg-canvas)] px-1.5 py-0.5 text-[var(--fg-primary)]">
                        active demo
                      </span>
                    )}
                  </div>
                  <h4 className="baton-display mt-4 text-lg font-medium text-[var(--fg-primary)]">{title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--fg-secondary)]">{body}</p>
                </button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
